import { isObject } from '@vueuse/core'
import { captureException } from '@sentry/vue'
import { useToast } from '@/composables/useToast'

class ServerError extends Error {
  response?: { status: number }

  constructor(message: string, status?: number) {
    super(message)
    this.name = 'ServerError'
    if (status)
      this.response = { status }
  }
}

/**
 * The request reached the API and came back a failure: `message` is the API's own reason, written
 * for the employer. Anything else (no connection, an expired session) rejects with another error.
 */
export const isApiError = (error: unknown): error is ServerError => error instanceof ServerError

type LegacyErrorResponse = {
  status: number
  message: string
  errors: Record<string, string[]>
}

type ProblemDetailsResponse = {
  status: number
  detail?: string
  extensions?: { code?: number }
}

const isLegacyErrorResponse = (response: unknown): response is LegacyErrorResponse =>
  isObject(response) && 'errors' in response

const isProblemDetails = (response: unknown): response is ProblemDetailsResponse =>
  isObject(response) && 'detail' in response

const GENERIC_ERROR = 'Er is een fout opgetreden. Probeer het later opnieuw.'

/**
 * Shared function to handle errors from an API. Like Rendering error messages.
 *
 * The rejection carries the server's own message, so a caller that passes `silent` can show the
 * failure where it happened instead of in a toast.
 */
export async function handleErrorResponse(request: RequestInit, response: Response, overrideToast?: ReturnType<typeof useToast>, silent = false) {
  const toast = overrideToast ?? useToast()

  /** Toasts the message (unless the caller shows it itself) and rejects the request with it. */
  const fail = (message: string) => {
    if (!silent) {
      toast.error(message)
    }

    return Promise.reject(new ServerError(message, response.status))
  }

  if (response.status === 500) {
    return fail('Het verzoek kon niet worden verwerkt. Het development team, is op de hoogte gebracht.')
  }

  let responseBody: unknown

  try {
    responseBody = await response.json()
  } catch {
    return fail(GENERIC_ERROR)
  }

  if (isProblemDetails(responseBody)) {
    return fail(responseBody.detail ?? GENERIC_ERROR)
  }

  if (isLegacyErrorResponse(responseBody)) {
    const messages = Object.values(responseBody.errors).flat()

    if (messages.length === 0) {
      captureException(new ServerError('No error messages found'), {
        extra: {
          request: request,
          response: responseBody,
          status: response.status,
          url: response.url,
        },
      })

      return fail(GENERIC_ERROR)
    }

    if (!silent) {
      messages.forEach(message => toast.error(message))
    }

    return Promise.reject(new ServerError(messages.join(' '), response.status))
  }

  return fail(GENERIC_ERROR)
}